import { fmtHours } from './display-labels';

/**
 * raw_minutes → hours. A UNIT CONVERSION, not a formatter.
 *
 * The rendering is `fmtHours` from display-labels, which rejects null,
 * undefined and '' by identity BEFORE coercing — because `Number(null)` and
 * `Number('')` are both 0, and a NULL that becomes "0.00h" is the
 * silent-blank-field family landing on the one figure an approver signs for.
 * This function only moves the unit and preserves absence as `null`.
 *
 * Hoisted out of ClockInScreen, where it was written for the worker's own
 * shift list. ApprovalQueueScreen needed the same thing for the office's list
 * of everyone's, and a second copy is how one of them grows a `?? 0` — which
 * is exactly what ApprovalQueueScreen had before this landed.
 */
export function shiftHours(rawMinutes: number | string | null | undefined): number | null {
  if (rawMinutes === null || rawMinutes === undefined || rawMinutes === '') return null;
  const n = Number(rawMinutes);
  return Number.isFinite(n) ? n / 60 : null;
}

type Minutes = number | string | null | undefined;

const isAbsent = (v: Minutes): boolean => v === null || v === undefined || v === '';

/**
 * The minutes a shift is RECORDED at: `payable_minutes` when the shift carries
 * a rounding snapshot, else `raw_minutes`. Absence survives as null.
 *
 * `payable_minutes` is NULL on every shift closed while rounding was off
 * (api#823, 0423) — NULL means "use raw", never "zero" and never "unknown".
 * A `?? raw` would be wrong the other way round too: a real `0` payable is a
 * snapshot, not a missing one, so the test is by identity, not truthiness.
 */
export function minutesOfRecord(payableMinutes: Minutes, rawMinutes: Minutes): Minutes {
  return isAbsent(payableMinutes) ? rawMinutes : payableMinutes;
}

/**
 * "7.25h", or "7.25h (clocked 7.30h)" when a rounding snapshot differs from
 * the raw punches, or "—" when there is nothing to show.
 *
 * THE ONE FORMATTER for a shift's hours on every payroll surface — the
 * worker's shift list, the approval queue's rows and toasts, and the detail
 * modal. A copy per screen is how one of them shows raw while the approver
 * next to it signs off the rounded figure.
 *
 * The raw figure appears ONLY when the two differ, so a tenant with rounding
 * off (every shift NULL) sees exactly what it saw before. The comparison is on
 * the minutes, not the rendered text: payable is always a multiple of 15 and
 * the smallest difference is one minute (0.017h), which two decimals always
 * show, so the parenthetical can never read "7.25h (clocked 7.25h)".
 *
 * A snapshot beside an absent raw figure — which the API's pair of writes
 * should never produce — prints "(clocked —)" rather than hiding the gap.
 */
const hoursLabel = (h: number | string | null | undefined): string => {
  const text = fmtHours(h);
  return text === '—' ? text : `${text}h`;
};

export function shiftHoursText(payableMinutes: Minutes, rawMinutes: Minutes): string {
  const hours = (m: Minutes): string => hoursLabel(shiftHours(m));
  if (isAbsent(payableMinutes)) return hours(rawMinutes);
  const record = hours(payableMinutes);
  if (!isAbsent(rawMinutes) && Number(rawMinutes) === Number(payableMinutes)) return record;
  return `${record} (clocked ${hours(rawMinutes)})`;
}

type Hours = number | string | null | undefined;

/**
 * The same rendering as `shiftHoursText`, for a figure the API has ALREADY
 * aggregated into hours: the payroll reports' `hours_of_record` +
 * `clocked_hours` pair (api#824 — hours-summary, pay-summary's timesheets
 * branch, cost-by-job, ot-threshold-status).
 *
 * THE API DECIDES WHETHER THE TWO DIFFER, NOT THIS FUNCTION. `clocked_hours`
 * arrives null unless it differs from `hours_of_record` after both are rounded
 * to two places (`reportHours` in the API's hours-of-record.ts), so the
 * parenthetical is drawn exactly when the field is present. Comparing the two
 * here would be a second answer to a question the payload already answered.
 *
 * Absent hours of record beside a clocked figure prints "— (clocked 7.30h)"
 * rather than hiding the one number that did arrive.
 */
export function reportHoursText(hoursOfRecord: Hours, clockedHours: Hours): string {
  const record = hoursLabel(hoursOfRecord);
  if (isAbsent(clockedHours)) return record;
  return `${record} (clocked ${hoursLabel(clockedHours)})`;
}
